import React, { createContext, useContext, useMemo } from 'react';

interface MatterContextValue {
  matterId: string | null;
}

const MatterContext = createContext<MatterContextValue | undefined>(undefined);

export const useMatterContext = () => {
  return useContext(MatterContext);
};

interface MatterProviderProps {
  matterId: string;
  children: React.ReactNode;
}

/**
 * MatterProvider - Provides matter ID context to components that use useMatter
 *
 * This is useful when rendering components in contexts where the route params
 * don't include matter_id (e.g., in modals or drawers).
 *
 * @example
 * ```tsx
 * <MatterProvider matterId={matterId}>
 *   <ComponentThatUsesUseMatter />
 * </MatterProvider>
 * ```
 */
export const MatterProvider: React.FC<MatterProviderProps> = ({
  matterId,
  children,
}) => {
  const value = useMemo(() => ({ matterId }), [matterId]);

  return (
    <MatterContext.Provider value={value}>{children}</MatterContext.Provider>
  );
};
